import React from 'react';
import { Box, Typography, TextField, Button } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useDispatch, useSelector } from 'react-redux';
import { cuteStarActions } from 'src/store/slices/cuteStarSlice';
import CuteStar from 'src/components/CuteStar/CuteStar';
import { paymentThunks, paymentActions } from 'src/store/slices/paymentSlice';

const ProductSelect = ({
  classes,
  currency,
  productLink
}) => {
  const dispatch = useDispatch();
  const starWrapperRef = React.useRef(null);
  const STAR_SIZE = 110;
  // Redux product list
  const productTenantSales = useSelector(s => s.payment.productTenantSales) || [];
  const verifyWorkOrderData = useSelector(s => s.payment.verifyWorkOrderData);
  const sku = useSelector(s => s.payment.productSKU);
  const price = useSelector(s => s.payment.productPrice);
  const workOrderId = useSelector(s => s.payment.workOrderId);
  const productId = useSelector(s => s.payment.productId); // added: current selected product id
  const [searchText, setSearchText] = React.useState('');
  const [page, setPage] = React.useState(0);
  const PAGE_SIZE = 8; // 5 -> 8
  const [verifying, setVerifying] = React.useState(false);
  const [workOrderVerified, setWorkOrderVerified] = React.useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = React.useState(null);
  const verifiedObject = verifyWorkOrderData?.ResponseObject;

  React.useEffect(() => {
    dispatch(cuteStarActions.setExpression('happy'));
    dispatch(cuteStarActions.setMessage('You can select a product on this page!'));
    dispatch(cuteStarActions.setShowBubble(true));
    // Always fetch product list
    dispatch(paymentThunks.GetProductTenantSales());
  }, [dispatch]);
  // Position star top-center
  React.useLayoutEffect(() => {
    let frameId = null;
    let tries = 0;
    const MAX_TRIES = 15;
    const place = () => {
      if (!starWrapperRef.current) return;
      const rect = starWrapperRef.current.getBoundingClientRect();
      const width = rect.width;
      // Width hazır değilse tekrar dene
      if ((width === 0 || width === undefined) && tries < MAX_TRIES) {
        tries++;
        frameId = requestAnimationFrame(place);
        return;
      }
      const left = Math.max(0, (width - STAR_SIZE) / 2);
      const top = 0;
      dispatch(cuteStarActions.setPosition({ left, top }));
    };
    place();
    // Boyut değişimlerine tepki ver (örn. responsive container)
    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => place());
      if (starWrapperRef.current) ro.observe(starWrapperRef.current);
    } else {
      const onResize = () => place();
      window.addEventListener('resize', onResize);
      ro = { disconnect: () => window.removeEventListener('resize', onResize) };
    }
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      ro && ro.disconnect && ro.disconnect();
    };
  }, [dispatch, STAR_SIZE]);

  // Filter + pagination
  const normalizedSearch = searchText.trim().toLowerCase();
  const filtered = productTenantSales.filter(p =>
    !normalizedSearch ||
    (p.ProductSKU || '').toLowerCase().includes(normalizedSearch)
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const pageStart = page * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const goPrev = () => setPage(p => Math.max(0, p - 1));
  const goNext = () => setPage(p => Math.min(totalPages - 1, p + 1));
  React.useEffect(() => { // Reset page when search changes
    setPage(0);
  }, [searchText]);

  const handleSelect = (r) => {
    // removed debugger
    dispatch(cuteStarActions.setExpression('happy'));
    dispatch(cuteStarActions.setMessage('If your product sale is associated with a WorkOrder please enter its WorkOrderId.'));
    dispatch(cuteStarActions.setShowBubble(true));
    const selectedProductId = (r && r.ProductId != null) ? r.ProductId : (r?.Id ?? 0);
    dispatch(paymentActions.setProductName(r.ProductName));
    dispatch(paymentActions.setProductSKU(r.ProductSKU));
    dispatch(paymentActions.setProductId(selectedProductId));
    dispatch(paymentActions.setProductPrice(String(r.Price)));
  };

  const handleVerifyWorkOrder = () => {
    setWorkOrderVerified(false);
    if (!workOrderId.trim()) {
      dispatch(cuteStarActions.setExpression('wink'));
      dispatch(cuteStarActions.setMessage('Please enter a WorkOrderId first.'));
      dispatch(cuteStarActions.setShowBubble(true));
      return;
    }
    setVerifying(true);
    dispatch(paymentActions.clearVerifyWorkOrder());
    dispatch(paymentThunks.VerifyWorkOrder(workOrderId))
      .then(action => {
        setVerifying(false);
        const payload = action.payload;
        const ok = payload && (payload.Code === '200' || payload.Success === true || payload.ResponseObject);
        if (ok) {
          dispatch(cuteStarActions.setExpression('happy'));
          dispatch(cuteStarActions.setMessage(`WorkOrder ${workOrderId} verified!`));
        } else {
          dispatch(cuteStarActions.setExpression('cry'));
          dispatch(cuteStarActions.setMessage(`WorkOrder could not be verified: ${payload?.Message || 'unknown error'}`));
        }
        dispatch(cuteStarActions.setShowBubble(true));
      })
      .catch(err => {
        setVerifying(false);
        dispatch(cuteStarActions.setExpression('cry'));
        dispatch(cuteStarActions.setMessage(`Verification error: ${err?.message || 'error'}`));
        dispatch(cuteStarActions.setShowBubble(true));
      });
  };

  // Doğrulama durumu slice verisinden kalıcı olarak türet
  React.useEffect(() => {
    if (verifiedObject && String(verifiedObject.WorkOrderId) === String(workOrderId)) {
      setWorkOrderVerified(true);
      setSelectedWorkOrder(verifiedObject.WorkOrderId);
    } else {
      setWorkOrderVerified(false);
      setSelectedWorkOrder(null);
    }
  }, [verifiedObject, workOrderId]);

  const handleClearProduct = () => {
    dispatch(paymentActions.clearProductSelection());
    dispatch(cuteStarActions.setExpression('happy'));
    dispatch(cuteStarActions.setMessage('Product cleared, you can select again.'));
    dispatch(cuteStarActions.setShowBubble(true));
  };
  const handleClearWorkOrder = () => {
    dispatch(paymentActions.setWorkOrderId(''));
    setWorkOrderVerified(false);
    setSelectedWorkOrder(null);
    dispatch(paymentActions.clearVerifyWorkOrder());
    // New: clear created product order and all consumer-related data
    dispatch(paymentActions.clearCreatedProductOrder());
    dispatch(paymentActions.clearCreatedCustomer());
    dispatch(paymentActions.clearConsumerSearch());
    dispatch(cuteStarActions.setExpression('wink'));
    dispatch(cuteStarActions.setMessage('WorkOrder cleared, related order & customer data removed.'));
    dispatch(cuteStarActions.setShowBubble(true));
  };

  return (
    <Box mt={3}>
      {/* --- Glass Header Panel (Star + Title) --- */}
      <Box
        style={{
          position:'relative',
          padding:'28px 28px 22px',
          borderRadius:28,
          background:'linear-gradient(145deg,rgba(250,252,255,.85),rgba(230,240,255,.72))',
          backdropFilter:'blur(6px)',
          WebkitBackdropFilter:'blur(6px)',
          border:'1px solid rgba(150,180,215,.55)',
          boxShadow:'0 6px 26px -8px rgba(25,55,110,.25)',
          overflow:'hidden',
          transition:'box-shadow .4s ease, transform .4s ease'
        }}
        onMouseEnter={e=>{
          e.currentTarget.style.boxShadow='0 12px 38px -10px rgba(25,55,110,.35)';
          e.currentTarget.style.transform='translateY(-3px)';
        }}
        onMouseLeave={e=>{
          e.currentTarget.style.boxShadow='0 6px 26px -8px rgba(25,55,110,.25)';
          e.currentTarget.style.transform='none';
        }}
      >
        <div style={{
          position:'absolute',
          top:0,left:0,right:0,
          height:6,
          background:'linear-gradient(90deg,#3b82f6,#60a5fa,#93c5fd)'
        }}/>
        <div
          ref={starWrapperRef}
          className="force-visible-star"
          style={{
            position:'relative',
            width:'100%',
            minHeight: STAR_SIZE + 10,
            marginBottom:4,
            overflow:'visible'
          }}
        >
          <style>{`.force-visible-star .cute-star-mascot{visibility:visible!important;opacity:1!important;}`}</style>
          <CuteStar size={STAR_SIZE} floating />
        </div>
        <Typography
          variant="h6"
          style={{
            fontWeight:700,
            letterSpacing:'.6px',
            color:'#1d4a7a',
            marginTop:4
          }}
        >Product Selection</Typography>

        {/* Control Bar (yeniden tasarlandı) */}
        <Box
          mt={2}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          style={{
            background:'linear-gradient(135deg,#ffffff,#f2f7fb)',
            border:'1px solid #d5e3ef',
            borderRadius:18,
            padding:'14px 18px',
            gap:14
          }}
        >
          <TextField
            label="SKU Search (Contains)"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search by characters contained in SKU..."
            style={{ flex:'0 0 200px', minWidth:180, maxWidth:220 }}
          />
          <div
            style={{
              display:'flex',
              alignItems:'center',
              gap:10,
              flexWrap:'wrap',
              flex:1,
              minWidth:320
            }}
          >
            {/* Product badge */}
            <div
              style={{
                display:'flex',
                alignItems:'center',
                padding:'6px 14px',
                background: price ? 'linear-gradient(135deg,#0d47a1,#1565c0)' : '#eef3f7',
                color: price ? '#fff' : '#4a6475',
                borderRadius:14,
                fontSize:12,
                fontWeight:600,
                letterSpacing:'.5px',
                boxShadow: price
                  ? '0 4px 12px -4px rgba(13,71,161,.45)'
                  : 'inset 0 0 0 1px #d5e3ef',
                minHeight:40
              }}
            >
              {price ? `${sku} • ${price} ${currency}` : 'No price'}
              {price && (
                <HighlightOffIcon
                  onClick={handleClearProduct}
                  style={{
                    marginLeft:8,
                    fontSize:18,
                    cursor:'pointer',
                    color:'#ffdddd'
                  }}
                />
              )}
            </div>
            <TextField
              label="WorkOrderId"
              variant="outlined"
              size="small"
              value={workOrderId}
              onChange={e => {
                dispatch(paymentActions.setWorkOrderId(e.target.value));
                if (workOrderVerified) setWorkOrderVerified(false);
              }}
              style={{ flex:1 }}
              InputProps={{ style:{ height:40 } }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleVerifyWorkOrder}
              disabled={verifying}
              style={{
                height:40,
                minWidth:90,
                textTransform:'none',
                fontWeight:600
              }}
            >
              {verifying ? '...' : (workOrderVerified ? 'Verified' : 'Verify')}
            </Button>
            {/* WorkOrder badge (doğrulanmışsa) */}
            {workOrderVerified && selectedWorkOrder && (
              <div
                style={{
                  display:'flex',
                  alignItems:'center',
                  padding:'6px 14px',
                  background:'linear-gradient(135deg,#14532d,#198754)',
                  color:'#fff',
                  borderRadius:14,
                  fontSize:12,
                  fontWeight:600,
                  letterSpacing:'.5px',
                  boxShadow:'0 4px 12px -4px rgba(0,120,60,.45)',
                  minHeight:40
                }}
              >
                WO • {selectedWorkOrder}
                <HighlightOffIcon
                  onClick={handleClearWorkOrder}
                  style={{
                    marginLeft:8,
                    fontSize:18,
                    cursor:'pointer',
                    color:'#ffe2e2'
                  }}
                />
              </div>
            )}
          </div>
        </Box>

        {/* WorkOrder helper text */}
        <Typography
          variant="caption"
          style={{
            marginTop:6,
            display:'block',
            fontWeight:500,
            color: workOrderVerified ? '#0d7a30' : '#466072'
          }}
        >
          {workOrderVerified
            ? 'WorkOrder ' + selectedWorkOrder + ' verified'
            : 'Optional: WorkOrder Id you want to link to this sale'}
        </Typography>
      </Box>

      {/* Product List */}
      {filtered.length > 0 && (
        <>
          <div className={classes.divider} style={{ marginTop:32 }} />
          <div
            className={classes.resultList}
            style={{
              display:'grid',
              gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',
              gap:18
            }}
          >
            {pageItems.map(r => {
              const rId = (r && r.ProductId != null) ? r.ProductId : (r?.Id ?? 0);
              const selected = rId === productId; // use productId to mark selection
              return (
                <div
                  key={r.Id || r.ProductSKU}
                  className={`${classes.productCard} ${selected ? classes.selected : ''}`}
                  onClick={() => handleSelect(r)}
                  style={{
                    background:'linear-gradient(140deg,#ffffff,#f6f9fc)',
                    border:'1px solid ' + (selected ? '#1976d2' : '#d5e2ec'),
                    boxShadow:selected
                      ? '0 0 0 2px rgba(25,118,210,.25),0 8px 24px -8px rgba(25,55,110,.35)'
                      : '0 4px 16px -6px rgba(25,55,110,.18)',
                    transition:'box-shadow .35s ease, transform .35s ease',
                    borderRadius:18,
                    padding:14
                  }}
                  onMouseEnter={e=>{
                    if(!selected){
                      e.currentTarget.style.boxShadow='0 10px 26px -6px rgba(25,55,110,.28)';
                      e.currentTarget.style.transform='translateY(-3px)';
                    }
                  }}
                  onMouseLeave={e=>{
                    if(!selected){
                      e.currentTarget.style.boxShadow='0 4px 16px -6px rgba(25,55,110,.18)';
                      e.currentTarget.style.transform='none';
                    }
                  }}
                >
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <Typography variant="body2" style={{ fontWeight:700, fontSize:'0.9rem', color:'#103a63' }}>
                      {r.ProductSKU + " - " + r.ProductName}
                    </Typography>
                  </div>
                  <div className={classes.priceBadgeWrap}>
                    <div className={classes.priceBadge} style={{
                      background:'#0d47a1',
                      borderRadius:16,
                      padding:'10px 16px 12px'
                    }}>
                      <span className={classes.priceValue}>{r.Price}</span>
                      <span className={classes.priceCur}>{currency}</span>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ marginTop:10 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<LaunchIcon style={{ fontSize:16 }} />}
                          className={classes.linkBtn}
                          style={{ textTransform:'none', fontSize:11, padding:'4px 10px' }}
                          onClick={(e) => { e.stopPropagation(); window.open(productLink, '_blank', 'noopener'); }}
                        >
                          Product Link
                        </Button>
                      </div>
                    </div>
                  </div>
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      size="small"
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={(e) => { e.stopPropagation(); handleSelect(r); }}
                      style={{ minWidth:96, fontWeight:600, textTransform:'none' }}
                    >
                      {selected ? 'Selected' : 'Select'}
                    </Button>
                  </Box>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <Box
            mt={3}
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{
              gap:14,
              background:'linear-gradient(135deg,#ffffff,#f2f7fb)',
              border:'1px solid #d5e3ef',
              padding:'10px 18px',
              borderRadius:28,
              width:'fit-content',
              margin:'24px auto 0'
            }}
          >
            <Button
              size="small"
              onClick={goPrev}
              disabled={page===0}
              style={{ textTransform:'none', minWidth:70 }}
            >Prev</Button>
            <Typography variant="caption" style={{ fontWeight:600, color:'#1d4a7a' }}>
              Page {page+1} / {totalPages}
            </Typography>
            <Button
              size="small"
              onClick={goNext}
              disabled={page===totalPages-1}
              style={{ textTransform:'none', minWidth:70 }}
            >Next</Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProductSelect;
